import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Time periods with their hour ranges and ambient lighting settings
 */
const TIME_PERIODS = {
  dawn: {
    startHour: 5,
    endHour: 7,
    warmth: 'rgba(255, 220, 180, 0.15)', // Cool golden, hopeful
    secondaryWarmth: 'rgba(200, 180, 220, 0.08)', // Hint of purple/blue from night
    intensity: 0.7,
    vignetteStrength: 0.2,
    vignetteColor: 'rgba(40, 35, 50, 0.25)',
    particleOpacity: 0.5,
    lightBeamOpacity: 0.4,
  },
  morning: {
    startHour: 7,
    endHour: 12,
    warmth: 'rgba(255, 250, 240, 0.12)', // Bright, clear light
    secondaryWarmth: 'rgba(255, 245, 230, 0.08)',
    intensity: 1.0,
    vignetteStrength: 0.12,
    vignetteColor: 'rgba(30, 25, 20, 0.18)',
    particleOpacity: 0.7,
    lightBeamOpacity: 0.6,
  },
  afternoon: {
    startHour: 12,
    endHour: 17,
    warmth: 'rgba(255, 240, 210, 0.14)', // Warm, full daylight
    secondaryWarmth: 'rgba(255, 235, 200, 0.10)',
    intensity: 0.95,
    vignetteStrength: 0.15,
    vignetteColor: 'rgba(35, 28, 20, 0.20)',
    particleOpacity: 0.8,
    lightBeamOpacity: 0.7,
  },
  dusk: {
    startHour: 17,
    endHour: 20,
    warmth: 'rgba(255, 200, 140, 0.20)', // Golden hour, rich amber
    secondaryWarmth: 'rgba(255, 180, 120, 0.12)',
    intensity: 0.8,
    vignetteStrength: 0.25,
    vignetteColor: 'rgba(50, 30, 20, 0.30)',
    particleOpacity: 0.9,
    lightBeamOpacity: 0.85,
  },
  evening: {
    startHour: 20,
    endHour: 22,
    warmth: 'rgba(255, 180, 120, 0.18)', // Warm candlelight feeling
    secondaryWarmth: 'rgba(200, 150, 100, 0.10)',
    intensity: 0.6,
    vignetteStrength: 0.35,
    vignetteColor: 'rgba(20, 15, 10, 0.40)',
    particleOpacity: 0.6,
    lightBeamOpacity: 0.3,
  },
  night: {
    startHour: 22,
    endHour: 5,
    warmth: 'rgba(180, 160, 140, 0.10)', // Soft, muted candlelight
    secondaryWarmth: 'rgba(100, 90, 120, 0.06)', // Subtle moonlight blue
    intensity: 0.4,
    vignetteStrength: 0.45,
    vignetteColor: 'rgba(10, 8, 12, 0.50)',
    particleOpacity: 0.3,
    lightBeamOpacity: 0.15,
  },
};

/**
 * Get the time period for a given hour
 * @param {number} hour - The hour (0-23)
 * @returns {string} The time period name
 */
const getTimePeriod = (hour) => {
  if (hour >= 5 && hour < 7) return 'dawn';
  if (hour >= 7 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 20) return 'dusk';
  if (hour >= 20 && hour < 22) return 'evening';
  return 'night';
};

/**
 * Interpolate between two time periods for smooth transitions
 * @param {object} from - Starting period settings
 * @param {object} to - Ending period settings
 * @param {number} progress - Progress between 0 and 1
 * @returns {object} Interpolated settings
 */
const interpolateSettings = (from, to, progress) => {
  return {
    intensity: from.intensity + (to.intensity - from.intensity) * progress,
    vignetteStrength: from.vignetteStrength + (to.vignetteStrength - from.vignetteStrength) * progress,
    particleOpacity: from.particleOpacity + (to.particleOpacity - from.particleOpacity) * progress,
    lightBeamOpacity: from.lightBeamOpacity + (to.lightBeamOpacity - from.lightBeamOpacity) * progress,
    warmth: from.warmth, // Colors don't interpolate smoothly, use discrete values
    secondaryWarmth: from.secondaryWarmth,
    vignetteColor: from.vignetteColor,
  };
};

/**
 * Custom hook to detect time of day and provide ambient lighting settings
 * @param {number} updateInterval - How often to check time (in ms), default 60000 (1 minute)
 * @returns {object} { period, hour, minute, settings }
 */
export const useTimeOfDay = (updateInterval = 60000) => {
  // Get current time
  const getCurrentTime = useCallback(() => {
    const now = new Date();
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
    };
  }, []);

  const [time, setTime] = useState(getCurrentTime);

  // Update time periodically
  useEffect(() => {
    // Initial update
    setTime(getCurrentTime());

    // Set up interval
    const intervalId = setInterval(() => {
      setTime(getCurrentTime());
    }, updateInterval);

    return () => clearInterval(intervalId);
  }, [getCurrentTime, updateInterval]);

  // Calculate current period and settings
  const { period, settings } = useMemo(() => {
    const currentPeriod = getTimePeriod(time.hour);
    const periodSettings = TIME_PERIODS[currentPeriod];

    // Calculate transition progress within the current hour
    // This creates smooth transitions at period boundaries
    const minuteProgress = time.minute / 60;

    // Check if we're near a transition point (within 30 minutes of period change)
    const periods = ['dawn', 'morning', 'afternoon', 'dusk', 'evening', 'night'];
    const currentIndex = periods.indexOf(currentPeriod);
    const nextPeriod = periods[(currentIndex + 1) % periods.length];
    const nextSettings = TIME_PERIODS[nextPeriod];

    // Get the end hour of current period
    const endHour = periodSettings.endHour;
    const isNearTransition = time.hour === endHour - 1 && time.minute >= 30;

    if (isNearTransition) {
      // Interpolate during transition (last 30 minutes of period)
      const transitionProgress = (time.minute - 30) / 30;
      return {
        period: currentPeriod,
        settings: interpolateSettings(periodSettings, nextSettings, transitionProgress),
      };
    }

    return {
      period: currentPeriod,
      settings: periodSettings,
    };
  }, [time.hour, time.minute]);

  // Apply CSS variables when settings change
  useEffect(() => {
    const root = document.documentElement;

    // Set CSS custom properties for time-based lighting
    root.style.setProperty('--time-warmth', settings.warmth);
    root.style.setProperty('--time-secondary-warmth', settings.secondaryWarmth);
    root.style.setProperty('--time-intensity', settings.intensity.toString());
    root.style.setProperty('--time-vignette-strength', settings.vignetteStrength.toString());
    root.style.setProperty('--time-vignette-color', settings.vignetteColor);
    root.style.setProperty('--time-particle-opacity', settings.particleOpacity.toString());
    root.style.setProperty('--time-light-beam-opacity', settings.lightBeamOpacity.toString());
    root.style.setProperty('--time-period', period);

    // Add a class to body for period-specific styling if needed
    document.body.setAttribute('data-time-period', period);
  }, [settings, period]);

  return {
    period,
    hour: time.hour,
    minute: time.minute,
    settings,
  };
};

export default useTimeOfDay;
